/**
 * Catalog resolution for the sales import: "артикул" cell → product, payment cell →
 * payment method. Pure — the caller passes the dicts it already holds in atoms.
 */

/* eslint-disable @typescript-eslint/no-explicit-any -- product/payment docs are untyped across Firestore and Supabase read modes */

import {
  type ParsedSalesRow,
  type ResolvedSalesRow,
  type SalesImportRowError,
} from './types'

export interface ProductIndex {
  byBarcode: Record<string, any>
  bySku: Record<string, any>
  byId: Record<string, any>
  byName: Record<string, any>
}

export const normalizeKey = (value: unknown): string | null => {
  if (value === null || value === undefined) return null
  const text = typeof value === 'number' && Number.isInteger(value)
    ? value.toFixed(0)
    : String(value)
  const trimmed = text.trim()
  return trimmed === '' ? null : trimmed
}

/**
 * Numeric keys are also indexed without leading zeros, so `0012` in the sheet finds
 * barcode `12` in the catalog (and the reverse).
 */
export const numericVariant = (key: string): string | null => {
  if (!/^\d+$/.test(key)) return null
  const stripped = key.replace(/^0+/, '')
  return stripped === '' ? '0' : stripped
}

const addKey = (index: Record<string, any>, key: string | null, product: any): void => {
  if (!key) return
  // First writer wins: a duplicate key in the catalog must not silently re-point an
  // earlier product, and the import cannot know which one the user meant.
  if (index[key] === undefined) index[key] = product
  const numeric = numericVariant(key)
  if (numeric && index[numeric] === undefined) index[numeric] = product
}

/** Build the lookup index once per import run, not once per row. */
export const buildProductIndex = (productsDict: Record<string, any>): ProductIndex => {
  const index: ProductIndex = { byBarcode: {}, bySku: {}, byId: {}, byName: {} }

  for (const product of Object.values(productsDict || {})) {
    if (!product) continue
    addKey(index.byBarcode, normalizeKey(product.barcode), product)
    addKey(index.bySku, normalizeKey(product.sku), product)
    addKey(index.byId, normalizeKey(product.id), product)
    addKey(index.byId, normalizeKey(product.firestoreId), product)
    addKey(index.byName, normalizeKey(product.name)?.toLowerCase() ?? null, product)
  }

  return index
}

/**
 * Resolve an article cell against the catalog: barcode → sku → id → exact name
 * (case-insensitive). Returns null when nothing matches; the caller reports the row.
 */
export const resolveProduct = (index: ProductIndex, article: string): any | null => {
  const key = normalizeKey(article)
  if (!key) return null

  const numeric = numericVariant(key)
  const candidates = numeric && numeric !== key ? [key, numeric] : [key]

  for (const dict of [index.byBarcode, index.bySku, index.byId]) {
    for (const candidate of candidates) {
      if (dict[candidate] !== undefined) return dict[candidate]
    }
  }
  return index.byName[key.toLowerCase()] ?? null
}

export interface ResolveProductsResult {
  rows: ResolvedSalesRow[]
  errors: SalesImportRowError[]
  /** Distinct article values that matched nothing, for the preview summary. */
  unknownArticles: string[]
}

export const resolveProductsForRows = (
  rows: ParsedSalesRow[],
  index: ProductIndex
): ResolveProductsResult => {
  const resolved: ResolvedSalesRow[] = []
  const errors: SalesImportRowError[] = []
  const unknownArticles: string[] = []

  for (const row of rows) {
    const product = resolveProduct(index, row.article)
    if (!product) {
      errors.push({ rowNumber: row.rowNumber, code: 'PRODUCT_NOT_FOUND', value: row.article })
      if (!unknownArticles.includes(row.article)) unknownArticles.push(row.article)
      continue
    }
    resolved.push({ ...row, product })
  }

  return { rows: resolved, errors, unknownArticles }
}

/**
 * Match a payment-method cell against the org's payment methods by name (case- and
 * whitespace-insensitive) or by id. Returns null when there is no match — the caller
 * falls back to the default the user chose in the UI.
 */
export const resolvePaymentMethod = (
  paymentMethodsDict: Record<string, any>,
  key: string | null
): any | null => {
  const normalized = normalizeKey(key)
  if (!normalized) return null

  const lowered = normalized.toLowerCase()
  for (const method of Object.values(paymentMethodsDict || {})) {
    if (!method) continue
    if (normalizeKey(method.id) === normalized) return method
    const name = normalizeKey(method.name ?? method.text)?.toLowerCase()
    if (name && name === lowered) return method
  }
  return null
}

/** Distinct, order-preserving client phone keys present in the parsed rows. */
export const collectClientKeys = (rows: ParsedSalesRow[]): string[] => {
  const keys: string[] = []
  for (const row of rows) {
    if (row.clientKey && !keys.includes(row.clientKey)) keys.push(row.clientKey)
  }
  return keys
}
